import React, { useContext } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Panel, PanelBody } from "../components/panel/panel.jsx";
import _ from "lodash";
import { AppSettings } from "./../config/app-settings.js";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import { formatDate, formatMoney } from "../helper/formatter.js";

const Dashboard = () => {
    const context = useContext(AppSettings);

    const terceros = useSelector((state) => state.tercero.items);

    const comprobantes = useSelector((state) => state.comprobante.items);
    const comprobantesActivos = _(comprobantes).reject("anulado").value();
    const comprobantesNoCancelados = _(comprobantesActivos).reject("cancelado").value();

    const historialVentaDateFrom = moment(moment().subtract(13, "months")).startOf("month"); //13 meses atras

    const dataHistorialVentas = comprobantesActivos
        .filter((c) => moment(c.fecha_emision) >= historialVentaDateFrom)
        .reduce((acc, c) => {
            const monthYear = moment(c.fecha_emision).format("MM/YYYY");
            const current = acc[monthYear] || {
                venta_sujeta: 0,
                venta_excenta: 0,
                venta_no_sujeta: 0,
                venta_total: 0,
            };
            current.venta_sujeta += c.suma_venta_sujeta;
            current.venta_excenta += c.suma_venta_excenta;
            current.venta_no_sujeta += c.suma_venta_no_sujeta;
            current.venta_total += c.suma_venta_sujeta + c.suma_venta_excenta + c.suma_venta_no_sujeta;
            acc[monthYear] = current;
            return acc;
        }, {});

    let top5Vencidas = comprobantesNoCancelados
        .sort((a, b) => new Date(a.fecha_vencimiento) - new Date(b.fecha_vencimiento))
        .slice(0, 8);

    const footer = (tooltipItems) => {
        const sum = tooltipItems.reduce((acc, tooltipItem) => acc + tooltipItem.parsed.y, 0);
        let vta_sujeta = tooltipItems.find((tooltipItem) => tooltipItem.dataset.label === "Ventas Sujetas");
        let iva = vta_sujeta?.parsed?.y * 0.13 || 0;
        let pagoCuenta = sum * 0.0175;
        return ` 
 ➥ Venta general: ${formatMoney(sum)} 
 ➥ IVA (13%): ${formatMoney(iva)}
 ➥ Pago a Cuenta (1.75%):  ${formatMoney(pagoCuenta)}`;
    };

    const showVentaSujeta = Object.values(dataHistorialVentas).some((d) => d.venta_sujeta > 0);
    const showVentaExcenta = Object.values(dataHistorialVentas).some((d) => d.venta_excenta > 0);
    const showVentaNoSujeta = Object.values(dataHistorialVentas).some((d) => d.venta_no_sujeta > 0);

    let datasets = [];

    if (showVentaSujeta)
        datasets.push({
            label: "Ventas Sujetas",
            borderWidth: 1,
            borderColor: context.color.green,
            backgroundColor: "rgba(" + context.color.limeRgb + ", .4)",
            data: _(dataHistorialVentas).mapValues("venta_sujeta").value(),
        });

    if (showVentaExcenta)
        datasets.push({
            label: "Ventas Excenta",
            borderWidth: 1,
            borderColor: context.color.blue,
            backgroundColor: "rgba(" + context.color.blueRgb + ", .4)",
            data: _(dataHistorialVentas).mapValues("venta_excenta").value(),
        });

    if (showVentaNoSujeta)
        datasets.push({
            label: "Ventas No Sujeta",
            borderWidth: 1,
            borderColor: context.color.pink,
            backgroundColor: "rgba(" + context.color.pinkRgb + ", .4)",
            data: _(dataHistorialVentas).mapValues("venta_no_sujeta").value(),
        });

    let barChart = {
        data: {
            labels: _.keys(dataHistorialVentas),
            datasets: datasets,
        },
        options: {
            interaction: {
                intersect: false,
                mode: "index",
            },
            plugins: {
                title: {
                    display: true,
                    text: "HISTORIAL DE VENTAS",
                },
                tooltip: {
                    callbacks: {
                        footer: footer,
                        label: function (context) {
                            let label = context.dataset.label || "";
                            let value = context.parsed.y;
                            return `${label} =  ${formatMoney(value)} `;
                        },
                    },
                },
            },
            animation: {
                duration: 1500,
            },
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    ticks: {
                        callback: (value) => formatMoney(value),
                    },
                },
            },
        },
    };

    let countCompPendVencidos = 0,
        countCompPendientes = 0,
        sumCompPendVencidos = 0,
        sumCompPendNoVencidos = 0;
    const now = Date.now();

    for (const c of comprobantesNoCancelados) {
        const isVencido = new Date(c.fecha_vencimiento) <= now;

        if (isVencido) {
            sumCompPendVencidos += c.pendiente;
            countCompPendVencidos++;
        } else {
            sumCompPendNoVencidos += c.pendiente;
            countCompPendientes++;
        }
    }

    return (
        <div>
            <ol className="breadcrumb float-xl-end">
                <li className="breadcrumb-item">
                    <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active">Active Page</li>
            </ol>
            <h1 className="page-header">
                ANALISIS DE DATOS <small>[Ventas]</small>
            </h1>

            <div className="row ">
                <div className="col-xl-4 col-md-6">
                    <div className="widget widget-stats bg-teal">
                        <div className="stats-icon stats-icon-lg">
                            <i className="fa fa-dollar-sign fa-fw"></i>
                        </div>
                        <div className="stats-content">
                            <div className="stats-title">COMPROBANTES POR COBRAR</div>
                            <div className="stats-number">{formatMoney(sumCompPendNoVencidos)}</div>
                        </div>
                        <div className="stats-desc">{countCompPendientes} comprobantes por vencer</div>
                    </div>
                </div>
                <div className="col-xl-4 col-md-6">
                    <div className="widget widget-stats bg-red">
                        <div className="stats-icon stats-icon-lg">
                            <i className="fa fa-warning fa-fw"></i>
                        </div>
                        <div className="stats-content">
                            <div className="stats-title">COMPROBANTES POR COBRAR VENCIDOS</div>
                            <div className="stats-number">{formatMoney(sumCompPendVencidos)}</div>
                        </div>
                        <div className="stats-desc">{countCompPendVencidos} comprobantes vencidos</div>
                    </div>
                </div>
                <div className="col-xl-4 col-md-6">
                    <div className="widget widget-stats bg-dark">
                        <div className="stats-icon stats-icon-lg">
                            <i className="fa fa-dollar-sign fa-fw"></i>
                        </div>
                        <div className="stats-content">
                            <div className="stats-title">COMPROBANTES PENDIENTES POR COBRAR</div>
                            <div className="stats-number">{formatMoney(sumCompPendNoVencidos + sumCompPendVencidos)}</div>
                        </div>
                        <div className="stats-desc">
                            {countCompPendientes + countCompPendVencidos} comprobantes pendientes de cobrar
                        </div>
                    </div>
                </div>
            </div>

            <Panel>
                <PanelBody>
                    <div className="row">
                        <div className="col-md-12" style={{ height: "270px" }}>
                            <Bar data={barChart.data} options={barChart.options} />
                        </div>
                    </div>
                </PanelBody>
            </Panel>

            <div className="col-12 col-xl-7 col-md-7">
                <div className="mb-10px mt-10px fs-10px">
                    <b className="text-dark">TOP 10 - COMPROBANTES VENCIDOS MAS ANTIGUOS</b>
                </div>
                <div className="table-responsive">
                    <table className="table table-bordered widget-table rounded ">
                        <thead>
                            <tr className="text-nowrap">
                                <th>COMPROBANTE</th>
                                <th width="40%">CLIENTE</th>
                                <th>FECHA EMISION</th>
                                <th>FECHA VENCIMIENTO</th>
                                <th>PENDIENTE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {_.map(top5Vencidas, (comp, i) => {
                                return (
                                    <tr key={i}>
                                        <td>
                                            <Link to={`/venta/comprobante/${comp.id}`} className="fw-bold text-nowrap">
                                                {comp.talonario.serie + comp.n_comprobante}
                                            </Link>
                                        </td>
                                        <td> {terceros[comp.terceroId]?.razonsocial} </td>
                                        <td>{formatDate(comp.fecha_emision)}</td>
                                        <td>{formatDate(comp.fecha_vencimiento)}</td>
                                        <td>{formatMoney(comp.pendiente)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
